import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Flame, HandHeart, MapPin, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";
import { BRAND, HERO_IMAGE, HISTORIA_IMAGE, SOCIALS, money } from "@/config";
import { Ornament } from "@/components/Ornament";
import { DecoShapes } from "@/components/DecoShapes";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.035 } } };
const letterAnim = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

function StaggeredText({ text, className }) {
  return (
    <motion.span variants={container} initial="hidden" animate="show" className={className} aria-label={text}>
      {text.split("").map((ch, i) => (
        <motion.span key={i} variants={letterAnim} className="inline-block" aria-hidden="true">
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </motion.span>
  );
}

function HeroJar() {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      style={reduce ? {} : { rotateX, rotateY, transformPerspective: 1000 }}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid="hero-jar"
    >
      <div className={reduce ? "" : "animate-float"}>
        <img
          src={HERO_IMAGE}
          alt="Colección de frascos de salsas artesanales SoyDiVa con chiles habaneros frescos"
          className="w-full rounded-3xl border border-sand object-cover shadow-[0_30px_60px_rgba(224,30,38,0.18)]"
        />
      </div>
      <div className="absolute -bottom-5 -left-5 rounded-2xl border border-sand bg-white px-5 py-3 shadow-lg">
        <p className="font-script font-semibold text-2xl leading-none text-orange">100% hechas a mano</p>
        <p className="text-xs font-semibold text-clay">desde {BRAND.since} · {BRAND.city}</p>
      </div>
    </motion.div>
  );
}

const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=salsas+diva+merida+rese%C3%B1as";
const GOOGLE_REVIEW_WRITE_URL = "https://share.google/7ZjlYj6FeAH0600lS";

const TESTIMONIALS = [
  { name: "Claudia Delgadillo", text: "Deliciosas!!! La crema de habanero súper rica y la de rajas con piña es dulce pero tiene un sabor muy rico, probé la de ajo y es cremosita, me encantaron!", dish: "★★★★★ · Reseña de Google" },
  { name: "Victoria Valdés", text: "Delis todas las salsas, mi favorita la macha, la de mi marido la de chapulines. Súper producto, 100% recomendado.", dish: "★★★★★ · Reseña de Google" },
  { name: "Karyme Cravioto Sanchez", text: "Excelentes salsas!!! Deliciosas si te encanta comer picante, 100% recomendadas.", dish: "★★★★★ · Reseña de Google" },
  { name: "Isa Perez", text: "Muy ricas, son salsas que puedes combinar con varias comidas y el picor de algunas salsas es muy rico, no opaca. Me gusta.", dish: "★★★★★ · Reseña de Google" },
  { name: "Cliente de Google", text: "Muy ricas las salsas!!!", dish: "★★★★★ · Reseña de Google" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [kit, setKit] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then(({ data }) => {
        setFeatured(data.filter((p) => p.category !== "kit").slice(0, 4));
        setKit(data.find((p) => p.category === "kit") || null);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <DecoShapes />
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:py-24">
          <div>
            <StaggeredText
              text={BRAND.tagline}
              className="font-script text-3xl font-semibold text-orange sm:text-4xl"
            />
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-4 font-script text-4xl font-semibold leading-[1.05] tracking-tight text-cocoa sm:text-5xl lg:text-6xl text-balance"
            >
              Salsas artesanales con sazón de familia
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-6 max-w-md text-base leading-relaxed text-clay md:text-lg"
            >
              Somos una familia de Mérida que desde {BRAND.since} pone sazón e ilusión en cada bocado.
              Ingredientes artesanales, recetas de la casa y mucho amor yucateco.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Button asChild className="rounded-full bg-habanero px-8 py-6 text-base font-bold text-white transition-colors hover:bg-habanero-dark" data-testid="hero-shop-button">
                <Link to="/tienda">Ver la tienda <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-cocoa px-8 py-6 text-base font-bold text-cocoa transition-colors hover:bg-cocoa hover:text-white" data-testid="hero-story-button">
                <Link to="/historia">Nuestra historia</Link>
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-10 flex flex-wrap gap-6 text-xs font-semibold text-clay"
            >
              <span className="flex items-center gap-2"><HandHeart size={16} className="text-leaf" /> 100% artesanal</span>
              <span className="flex items-center gap-2"><Flame size={16} className="text-habanero" /> Habanero de la península</span>
              <span className="flex items-center gap-2"><MapPin size={16} className="text-habanero-dark" /> {BRAND.city}</span>
            </motion.div>
          </div>
          <HeroJar />
        </div>
      </section>

      {/* DESTACADOS */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-script font-semibold text-2xl text-orange">Las favoritas de la casa</p>
            <h2 className="mt-2 font-script text-3xl font-semibold text-cocoa sm:text-4xl">Los consentidos</h2>
            <Ornament className="mt-3 justify-start text-terracotta" />
          </div>
          <Link to="/tienda" className="flex items-center gap-1 text-sm font-bold text-habanero transition-colors hover:text-habanero-dark" data-testid="featured-view-all">
            Ver todo el catálogo <ArrowRight size={16} />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* KIT DE DEGUSTACIÓN */}
      {kit && (
        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="grid items-center gap-10 overflow-hidden rounded-3xl border border-sand bg-surface p-8 sm:p-14 lg:grid-cols-2"
            data-testid="kit-section"
          >
            <div className="flex justify-center">
              <img
                src={kit.image}
                alt="Kit de degustación SoyDiVa — caja con cinco mini frascos de salsa"
                loading="lazy"
                className="max-h-80 w-auto rounded-2xl object-contain jar-shadow"
              />
            </div>
            <div>
              <p className="font-script font-semibold text-2xl text-orange">¿No sabes por cuál empezar?</p>
              <h2 className="mt-2 font-script text-3xl font-semibold text-cocoa sm:text-4xl text-balance">
                Kit de degustación
              </h2>
              <p className="mt-4 leading-relaxed text-clay">{kit.short}</p>
              <p className="mt-6 font-display text-4xl font-extrabold text-habanero" data-testid="kit-price">
                {money(kit.variants[0].price)}
              </p>
              <Button asChild className="mt-6 rounded-full bg-habanero px-8 py-6 font-bold text-white hover:bg-habanero-dark" data-testid="kit-cta">
                <Link to={`/producto/${kit.slug}`}>Descubrir el kit <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
            </div>
          </motion.div>
        </section>
      )}

      {/* HISTORIA BREVE */}
      <section className="bg-surface overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-8 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <img
              src={HISTORIA_IMAGE}
              alt="Manos preparando salsa artesanal en molcajete en una cocina de Mérida, Yucatán"
              loading="lazy"
              className="w-full rounded-3xl border border-sand object-cover shadow-xl"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <p className="font-script font-semibold text-2xl text-orange">Herencia y tradición que es toda una explosión de sabor y sensación</p>
            <h2 className="mt-2 font-script text-3xl font-semibold text-cocoa sm:text-4xl text-balance">
              De la cocina de nuestra familia a tu mesa
            </h2>
            <p className="mt-6 leading-relaxed text-clay">
              Todo empezó en {BRAND.since}, en una cocina de Mérida donde el habanero es parte de la identidad.
              Cada frasco se elabora en tandas pequeñas, con ingredientes seleccionados y el mismo cuidado con
              el que cocinamos para los nuestros.
            </p>
            <Button asChild variant="outline" className="mt-8 rounded-full border-habanero px-6 text-habanero transition-colors hover:bg-habanero hover:text-white" data-testid="home-story-cta">
              <Link to="/historia">Conoce nuestra historia <ArrowRight className="ml-2" size={16} /></Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
        <p className="text-center font-script font-semibold text-2xl text-orange">El sabor que llena de alegría tu corazón</p>
        <h2 className="mt-2 text-center font-script text-3xl font-semibold text-cocoa sm:text-4xl">
          Clientes que ya son familia
        </h2>
        <Ornament className="mt-4 text-habanero" />
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="google-reviews-link"
          className="mx-auto mt-4 flex w-fit items-center gap-2 rounded-full border border-sand bg-white px-5 py-2 text-sm font-bold text-cocoa transition-colors hover:border-habanero hover:text-habanero"
        >
          <span className="text-corn" aria-hidden="true">★★★★★</span> 5.0 · 185 reseñas en Google
        </a>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="rounded-2xl border border-sand bg-white p-8 shadow-[0_8px_30px_rgba(224,30,38,0.08)]"
              data-testid={`testimonial-${i}`}
            >
              <Sparkles size={20} className="text-corn" />
              <blockquote className="mt-4 leading-relaxed text-cocoa">“{t.text}”</blockquote>
              <figcaption className="mt-6">
                <p className="font-bold text-cocoa">{t.name}</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-habanero">{t.dish}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* CTA DEJAR RESEÑA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="mt-10 rounded-3xl border border-sand bg-surface px-8 py-10 text-center"
          data-testid="review-cta"
        >
          <p className="font-script text-2xl font-semibold text-orange">¿Ya nos probaste?</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-clay">
            Tu opinión nos ayuda un montón a crecer y a que más gente conozca el sazón de Yucatán.
          </p>
          <Button asChild className="mt-5 rounded-full bg-habanero px-8 py-6 font-bold text-white transition-colors hover:bg-habanero-dark" data-testid="write-review-button">
            <a href={GOOGLE_REVIEW_WRITE_URL} target="_blank" rel="noopener noreferrer">
              <Star className="mr-2 fill-white" size={18} /> Déjanos tu reseña en Google
            </a>
          </Button>
        </motion.div>
      </section>

      {/* BANNER MAYOREO */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="grid items-center gap-8 rounded-3xl bg-cocoa px-8 py-14 text-white sm:px-14 lg:grid-cols-[1fr_auto]"
        >
          <div>
            <p className="font-script font-semibold text-2xl text-corn">SoyDiVa hace de lo cotidiano algo extraordinario</p>
            <h2 className="mt-2 font-script text-3xl font-semibold sm:text-4xl text-balance">
              Lleva el sazón SoyDiVa a tu negocio con precios de mayoreo
            </h2>
            <Ornament className="mt-4 justify-start text-terracotta" />
            <p className="mt-4 max-w-xl text-white/75">
              Cotizamos pedidos al mayoreo para restaurantes, tiendas gourmet y eventos. Cuéntanos qué necesitas y te armamos propuesta.
            </p>
          </div>
          <Button asChild className="rounded-full bg-corn px-8 py-6 text-base font-bold text-cocoa transition-colors hover:bg-cream" data-testid="home-wholesale-cta">
            <Link to="/mayoreo">Cotizar mayoreo <ArrowRight className="ml-2" size={18} /></Link>
          </Button>
        </motion.div>
      </section>

      {/* INSTAGRAM */}
      <section className="bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
          <p className="text-center font-script font-semibold text-2xl text-orange">Síguenos para más antojo</p>
          <h2 className="mt-2 text-center font-script text-3xl font-semibold text-cocoa sm:text-4xl">@salsasdiva en Instagram</h2>
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
            {featured.map((p) => (
              <motion.a
                key={p.slug}
                href={SOCIALS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-tile-${p.slug}`}
                whileHover={{ scale: 1.04, rotate: -1 }}
                className="group relative overflow-hidden rounded-2xl border border-sand"
              >
                <img src={p.image} alt={p.name} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-cocoa/60 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <span className="text-sm font-bold text-white">{p.name}</span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
